export default function CityDestinationsSection({
  city,
  data,
}: {
  city: string;
  data: {
    title: string;
    intro: string;
    stats: { stat: string; label: string }[];
    destinations: {
      name: string;
      context: string;
      urgency: string;
      icon: string;
    }[];
    crossLinksTitle?: string;
    crossLinksDesc?: string;
    crossLinks?: { name: string; route: string }[];
  };
}) {
  return (
    <section className="bg-white pt-8 md:pt-10 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Local Travel Intelligence
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            {data.title}
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            {data.intro}
          </p>
        </div>

        {/* Stat block row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {data.stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">
                {s.stat}
              </p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Destinations grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {data.destinations.map((d, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i
                    className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}
                  ></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                {d.context}
              </p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">
                  {d.urgency}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Cross links block */}
        {data.crossLinks && data.crossLinks.length > 0 && (
          <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-links-line text-[#1A365D] text-xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-[#1A365D] text-sm mb-1">
                  {data.crossLinksTitle || "Explore Other Oregon Service Areas"}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {data.crossLinksDesc ||
                    "We serve travelers across Oregon with the same expedited passport processing and document review expertise."}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                {data.crossLinks.map((link, i) => (
                  <a
                    key={i}
                    href={link.route}
                    className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap"
                  >
                    {link.name}{" "}
                    <i className="ri-arrow-right-line text-xs"></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}